<template>
  <Page :title="batch?.name || 'Review'">
    <template v-if="batch && !failure" #actions>
      <div class="flex flex-wrap items-center gap-4">
        <Button
          label="Refresh"
          type="Secondary"
          outline
          :disabled="manualRefreshing"
          data-cy="import-review-refresh"
          @click="refreshNow"
        />
        <ImportRowActions
          v-if="menuRow?.actions.length"
          :row="menuRow"
          data-cy="import-review-actions"
          @action="handleAction"
        />
      </div>
    </template>
    <ImportSteps
      v-if="!failure"
      :current="stepIndex"
      :skipped="batch && !batch.mappable ? [1] : []"
      class="mb-6"
    />

    <p
      v-if="failure === 'forbidden'"
      class="text-gray-600"
      data-cy="import-review-forbidden"
    >
      You don't have access to imports.
    </p>
    <div v-else-if="failure === 'not_found'" data-cy="import-review-not-found">
      <p class="text-gray-900">Import not found</p>
      <RouterLink
        to="/imports"
        class="text-sm font-medium text-blue-600 hover:underline"
      >
        Back to imports
      </RouterLink>
    </div>
    <p v-else-if="failure" class="text-red-600" data-cy="import-review-error">
      Couldn't load this import. Refresh to try again.
    </p>
    <div v-else-if="!batch" class="flex justify-center py-10">
      <Spinner message="Loading..." class="text-gray-500" />
    </div>

    <template v-else>
      <SummaryStrip :items="stripItems(batch)" class="mb-4" />

      <template v-if="batch.status === 'ready'">
        <div class="grid gap-4 md:grid-cols-3">
          <DryRunCard
            :class="showMapping ? 'md:col-span-2' : 'md:col-span-3'"
            :state="cardState"
            :dry-run="batch.dry_run"
            :rows="batch.rows"
            :can-run="batch.can.dry_run"
            :can-download="Boolean(batch.links.dry_run_report)"
            :can-cancel="Boolean(batch.links.cancel_dry_run)"
            :busy="requestingDryRun || cancelling"
            :queued="queued"
            :problems-so-far="problemsSoFar"
            @run="runDryRun"
            @download="downloadReport"
            @cancel="cancelDryRun"
          />
          <MappingCard
            v-if="showMapping"
            :pairs="batch.mapping_summary ?? []"
            :can-edit="batch.can.edit_mapping"
            @edit="openEdit"
          />
        </div>

        <div
          v-if="batch.can.start_import"
          class="mt-4 flex items-center gap-2 md:justify-end md:gap-4"
          :class="isMobile ? 'flex-col items-stretch' : ''"
          data-cy="import-review-footer"
        >
          <span
            v-if="skippedRows"
            class="text-sm text-gray-600"
            data-cy="rows-skipped"
          >
            {{ rowsText(skippedRows) }} will be skipped
          </span>
          <Button
            :label="startLabel"
            :disabled="startDisabled"
            :class="isMobile ? 'w-full' : ''"
            data-cy="start-import"
            @click="confirmingStart = true"
          />
        </div>
      </template>

      <div
        v-else
        class="rounded-lg border border-gray-200 bg-white p-4 md:p-5"
        data-cy="import-review-status"
      >
        <div class="flex flex-wrap items-center gap-3 text-sm">
          <ImportStatusChip :status="batch.status" />
          <i
            v-if="isPolling(batch)"
            class="far fa-circle-notch spinner text-blue-600"
            aria-hidden="true"
          />
          <span
            :class="statusLine.error ? 'text-red-600' : 'text-gray-700'"
            data-cy="import-review-status-text"
          >
            {{ statusLine.text }}
          </span>
          <button
            v-if="batch.links.cancel_import"
            type="button"
            class="inline-flex items-center gap-1 font-medium text-blue-600 hover:underline disabled:opacity-50 md:ml-auto"
            :disabled="cancelling"
            data-cy="cancel-import"
            @click="confirmingCancelImport = true"
          >
            <i class="fas fa-stop text-xs" aria-hidden="true" />
            Cancel import
          </button>
        </div>
        <RouterLink
          v-if="statusLink"
          :to="statusLink.to"
          class="mt-3 inline-block text-sm font-medium text-blue-600 hover:underline"
          data-cy="import-review-link"
        >
          {{ statusLink.label }}
        </RouterLink>
      </div>

      <ResultsCard
        v-if="finishedResults"
        :results="finishedResults"
        class="mt-4"
      />
    </template>

    <Modal
      v-if="confirmingStart && batch"
      :model-value="true"
      title="Start import?"
      size="small"
      @update:model-value="confirmingStart = false"
    >
      <p class="text-gray-700" data-cy="start-import-body">
        {{ rowsText(batch.rows ?? 0) }} from {{ batch.name }} will be imported.
      </p>
      <template #footer>
        <div class="flex justify-end gap-2 p-4">
          <Button
            label="Cancel"
            type="Secondary"
            outline
            data-cy="start-import-cancel"
            @click="confirmingStart = false"
          />
          <Button
            :label="startLabel"
            data-cy="start-import-confirm"
            @click="confirmStart"
          />
        </div>
      </template>
    </Modal>

    <Modal
      v-if="confirmingEdit"
      :model-value="true"
      title="Edit mapping?"
      size="small"
      @update:model-value="confirmingEdit = false"
    >
      <p class="text-gray-700" data-cy="edit-mapping-body">
        Editing the mapping reloads the file and discards the dry run.
      </p>
      <p
        v-if="editError"
        class="mt-2 text-sm text-red-600"
        role="alert"
        data-cy="edit-mapping-error"
      >
        {{ editError }}
      </p>
      <template #footer>
        <div class="flex justify-end gap-2 p-4">
          <Button
            label="Cancel"
            type="Secondary"
            outline
            data-cy="edit-mapping-cancel"
            @click="confirmingEdit = false"
          />
          <Button
            label="Edit mapping"
            :disabled="resettingMapping"
            data-cy="edit-mapping-confirm"
            @click="confirmEdit"
          />
        </div>
      </template>
    </Modal>

    <ReplaceFileModal
      v-if="batch?.replace_file && batch.links.replace_file"
      :open="replacingFile"
      :upload="batch.replace_file"
      :url="batch.links.replace_file"
      @cancel="replacingFile = false"
      @replaced="fileReplaced"
    />

    <WipeImportModal
      :open="confirmingWipe"
      :name="batch?.name ?? ''"
      @cancel="confirmingWipe = false"
      @confirm="confirmWipe"
    />

    <CancelImportModal
      :open="confirmingCancelImport"
      :name="batch?.name ?? ''"
      @cancel="confirmingCancelImport = false"
      @confirm="confirmCancelImport"
    />
  </Page>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

import Page from "@/components/Page.vue";
import Button from "@/components/Seranova/Buttons/Button.vue";
import Modal from "@/components/Seranova/Modal/Modal.vue";
import Spinner from "@/components/Spinner.vue";
import { useBreakpoints } from "@/composables/useBreakpoints";
import CancelImportModal from "@/DataImports/CancelImportModal.vue";
import ImportSteps from "@/DataImports/flow/ImportSteps.vue";
import ReplaceFileModal from "@/DataImports/flow/ReplaceFileModal.vue";
import DryRunCard from "@/DataImports/flow/review/DryRunCard.vue";
import MappingCard from "@/DataImports/flow/review/MappingCard.vue";
import ResultsCard from "@/DataImports/flow/review/ResultsCard.vue";
import {
  finishedProblems,
  type ImportBatch,
  isPolling,
  menuRowFor,
  pollIntervalMs,
  rowsText,
  startedAtMs,
  stripItems
} from "@/DataImports/flow/review/reviewBatch";
import { usePolling } from "@/DataImports/flow/review/usePolling";
import SummaryStrip from "@/DataImports/flow/SummaryStrip.vue";
import ImportRowActions from "@/DataImports/ImportRowActions.vue";
import {
  dryRunActive,
  type HubFile,
  type ImportAction,
  type ImportRow,
  type ImportStatus,
  linkFor,
  progressInfo
} from "@/DataImports/importsHubRows";
import ImportStatusChip from "@/DataImports/ImportStatusChip.vue";
import WipeImportModal from "@/DataImports/WipeImportModal.vue";

// Dry-run kickoff jobs share a busy Sidekiq queue, so a new run can take minutes to appear.
const QUEUED_AFTER_MS = 30_000;
const OPTIMISTIC_MS = 600_000;
const WIPE_FOLLOW_UP_MS = 5_000;
const REPORT_PENDING =
  "Your validation report is being prepared — try again in a moment.";

const SETTLED: ImportStatus[] = [
  "done",
  "import_failed",
  "import_cancelled",
  "wiped"
];

type LoadFailure = "forbidden" | "not_found" | "error";
type ApiError = { response?: { status?: number; data?: { message?: string } } };

const store = useStore();
const router = useRouter();
const route = useRoute();
const { type: screenType } = useBreakpoints();

const batchType = String(route.params.batchType);
const batchId = String(route.params.id);

const batch = ref<ImportBatch | null>(null);
const failure = ref<LoadFailure | null>(null);
// `after` is the start time of the last dry run seen when Run dry run was clicked.
const optimistic = ref<{ after: number | null } | null>(null);
const queued = ref(false);
const requestingDryRun = ref(false);
const confirmingStart = ref(false);
const starting = ref(false);
const confirmingWipe = ref(false);
const confirmingCancelImport = ref(false);
const cancelling = ref(false);
const replacingFile = ref(false);
const confirmingEdit = ref(false);
const resettingMapping = ref(false);
const editError = ref("");
let optimisticTimers: ReturnType<typeof setTimeout>[] = [];

const isMobile = computed(() => screenType.value === "mobile");
const cardState = computed(() =>
  optimistic.value ? "running" : (batch.value?.dry_run?.state ?? "none")
);
const problemsSoFar = computed(() =>
  optimistic.value ? null : (batch.value?.dry_run?.problem_row_count ?? null)
);
const skippedRows = computed(() =>
  batch.value ? (finishedProblems(batch.value) ?? 0) : 0
);
const startLabel = computed(() =>
  skippedRows.value ? "Start import anyway" : "Start import"
);
const startDisabled = computed(
  () =>
    starting.value || dryRunActive(cardState.value) || batch.value?.rows === 0
);
// Start import is withheld from the menu exactly when the footer button is disabled.
const menuRow = computed(() =>
  batch.value
    ? menuRowFor(batch.value, startDisabled.value ? ["start_import"] : [])
    : null
);
const showMapping = computed(() => batch.value?.mappable);
// Import is current while it runs or after it failed or was cancelled; once done, every step is.
const stepIndex = computed(() => {
  const status = batch.value?.status;
  if (status === "done" || status === "wiped") return 4;
  return ["importing", "import_failed", "import_cancelled"].includes(
    status ?? ""
  )
    ? 3
    : 2;
});
const statusLine = computed(() => progressInfo(batch.value as ImportRow));
// Imports finished before results were stored carry none, so they keep just the status line.
const finishedResults = computed(() => {
  const current = batch.value;
  const stored = current?.progress.results;
  if (!current || !SETTLED.includes(current.status) || !stored?.results) {
    return null;
  }
  return { ...stored, results: stored.results };
});

const statusLink = computed(() => {
  const current = batch.value;
  if (!current) return null;
  const { status, mappable, links } = current;
  if (status === "needs_mapping" && links.map_page) {
    return { label: "Finish mapping", to: links.map_page };
  }
  if (status === "load_failed" && mappable && links.map_page) {
    return { label: "Fix mapping", to: links.map_page };
  }
  return { label: "Back to imports", to: links.hub };
});

const errorMessage = (error: unknown) =>
  (error as ApiError)?.response?.data?.message;

function failureOf(error: unknown): LoadFailure {
  const status = (error as ApiError)?.response?.status;
  if (status === 403) return "forbidden";
  return status === 404 ? "not_found" : "error";
}

function clearOptimistic() {
  optimistic.value = null;
  queued.value = false;
  optimisticTimers.forEach(clearTimeout);
  optimisticTimers = [];
}

const startedAfter = (run: ImportBatch["dry_run"], previous: number | null) => {
  const at = startedAtMs(run);
  return at !== null && (previous === null || at > previous);
};

function setOptimistic(after: number | null) {
  clearOptimistic();
  optimistic.value = { after };
  optimisticTimers = [
    setTimeout(() => {
      queued.value = true;
    }, QUEUED_AFTER_MS),
    setTimeout(clearOptimistic, OPTIMISTIC_MS)
  ];
}

function applyBatch(next: ImportBatch) {
  if (!batch.value) {
    store.dispatch("setBreadcrumbs", [
      { label: "Imports", url: "/imports" },
      { label: next.name }
    ]);
  }
  batch.value = next;
  if (optimistic.value && startedAfter(next.dry_run, optimistic.value.after)) {
    clearOptimistic();
  }
}

async function loadBatch() {
  try {
    const { data } = await store.dispatch("fetchImport", {
      batchType,
      id: batchId
    });
    applyBatch(data);
  } catch (error) {
    // A failed poll keeps what the page already shows.
    if (!batch.value) failure.value = failureOf(error);
  }
}

const pollMs = computed(() =>
  pollIntervalMs(batch.value, Boolean(optimistic.value))
);
const { run: refresh } = usePolling(pollMs, loadBatch);

const manualRefreshing = ref(false);
async function refreshNow() {
  manualRefreshing.value = true;
  try {
    await refresh();
  } finally {
    manualRefreshing.value = false;
  }
}

async function runDryRun() {
  const current = batch.value;
  if (!current?.links.import || requestingDryRun.value || optimistic.value)
    return;
  const before = startedAtMs(current.dry_run);
  requestingDryRun.value = true;
  try {
    await store.dispatch("performBatchDryRun", current.links.import);
    setOptimistic(before);
  } catch {
    // The API client shows the server's message; look again in case a run already exists.
    await refresh();
  } finally {
    requestingDryRun.value = false;
  }
}

async function downloadReport() {
  const url = batch.value?.links.dry_run_report;
  if (!url) return;
  try {
    const { ready, message } = await store.dispatch(
      "downloadDryRunReport",
      url
    );
    if (!ready && message) window.notify.success(message);
  } catch (error) {
    // A still-running report answers the readiness probe with a non-2xx, which is info, not failure.
    window.notify.success(errorMessage(error) ?? REPORT_PENDING);
  }
}

async function confirmStart() {
  const url = batch.value?.links.import;
  confirmingStart.value = false;
  if (!url || starting.value) return;
  starting.value = true;
  try {
    await store.dispatch("performBatchImport", url);
    router.push("/imports");
  } catch {
    await refresh();
  } finally {
    starting.value = false;
  }
}

let wipeFollowUp: ReturnType<typeof setTimeout> | null = null;
function clearWipeFollowUp() {
  if (wipeFollowUp) clearTimeout(wipeFollowUp);
  wipeFollowUp = null;
}

async function confirmWipe() {
  const url = batch.value?.links.wipe;
  confirmingWipe.value = false;
  if (!url) return;
  try {
    await store.dispatch("wipeImport", url);
    window.notify.success("Wipe started");
    // The wipe runs as a job, so look once more after it has had time to finish.
    clearWipeFollowUp();
    wipeFollowUp = setTimeout(refresh, WIPE_FOLLOW_UP_MS);
  } catch {
    // The API client already toasts the failure; the page still needs the new state.
  } finally {
    await refresh();
  }
}

async function cancelRun(url: string | undefined) {
  if (!url || cancelling.value) return;
  cancelling.value = true;
  try {
    await store.dispatch("cancelBatchRun", url);
  } catch {
    // The API client toasts a refused cancel; the page still needs the new state.
  } finally {
    cancelling.value = false;
    await refresh();
  }
}

const cancelDryRun = () => cancelRun(batch.value?.links.cancel_dry_run);

async function confirmCancelImport() {
  confirmingCancelImport.value = false;
  await cancelRun(batch.value?.links.cancel_import);
}

async function downloadErrors(url: string) {
  try {
    const { ready, message } = await store.dispatch("downloadBatchErrors", url);
    if (!ready && message) window.notify.success(message);
  } catch (error) {
    const message = errorMessage(error);
    if (message) window.notify.error(message);
  }
}

async function handleAction(action: ImportAction, file?: HubFile) {
  if (action === "download_file") {
    if (file) window.open(file.href, "_blank");
    return;
  }
  const row = menuRow.value;
  const url = row && linkFor(row, action);
  if (!url) return;
  switch (action) {
    case "finish_mapping":
    case "fix_mapping":
      router.push(url);
      break;
    case "start_import":
      confirmingStart.value = true;
      break;
    case "run_dry_run":
      await runDryRun();
      break;
    case "download_dry_run_report":
      await downloadReport();
      break;
    case "download_errors":
      await downloadErrors(url);
      break;
    case "wipe":
      confirmingWipe.value = true;
      break;
    case "replace_file":
      replacingFile.value = true;
      break;
    case "cancel_dry_run":
      await cancelDryRun();
      break;
    case "cancel_import":
      confirmingCancelImport.value = true;
      break;
  }
}

// The batch is back to mapping with the old load and dry run cleared, so Map is the next step.
function fileReplaced() {
  replacingFile.value = false;
  const mapPage = batch.value?.links.map_page;
  if (mapPage) router.push(mapPage);
  else refresh();
}

// The hub's Replace file lands here, where the upload details are.
async function openReplaceFromQuery() {
  await refresh();
  if (route.query.replace_file !== "1") return;
  router.replace({ query: {} });
  if (batch.value?.can.replace_file) replacingFile.value = true;
}

function openEdit() {
  editError.value = "";
  confirmingEdit.value = true;
}

async function confirmEdit() {
  const links = batch.value?.links;
  if (!links?.reset_mapping || !links.map_page || resettingMapping.value)
    return;
  resettingMapping.value = true;
  editError.value = "";
  try {
    await store.dispatch("resetColumnMapping", links.reset_mapping);
    confirmingEdit.value = false;
    router.push(links.map_page);
  } catch (error) {
    editError.value =
      errorMessage(error) ?? "Couldn't reset the mapping. Try again.";
    await refresh();
  } finally {
    resettingMapping.value = false;
  }
}

onMounted(openReplaceFromQuery);
onBeforeUnmount(() => {
  clearOptimistic();
  clearWipeFollowUp();
});
</script>
